"use client";

import type { CSSProperties } from "react";

import { cx } from "@/components/ui/dotmatrix-core";
import { useDotMatrixPhases } from "@/components/ui/dotmatrix-hooks";
import { styleOpacity, stylePx } from "@/components/ui/dotmatrix-core";
import { remapOpacityToTriplet } from "@/components/ui/dotmatrix-core";
import { useCyclePhase } from "@/components/ui/dotmatrix-hooks";
import { useDotMatrixMotionDisabled } from "@/components/ui/dotmatrix-hooks";
import type { DotMatrixCommonProps } from "@/components/ui/dotmatrix-core";

export type DotmTriangle13Props = DotMatrixCommonProps;

const MATRIX_SIZE = 7;
const BASE_OPACITY = 0.13;
const HIGH_OPACITY = 0.95;

const TRIANGLE_CELLS = new Set([
  "1,3",
  "2,2",
  "2,4",
  "3,1",
  "3,3",
  "3,5",
  "4,0",
  "4,2",
  "4,4",
  "4,6"
]);

/** Row serpent: base row left→right, row 3 right→left, mid rows alternate — reads as a zigzag zip. */
const SERPENT_PATH: ReadonlyArray<readonly [number, number]> = [
  [4, 0],
  [4, 2],
  [4, 4],
  [4, 6],
  [3, 5],
  [3, 3],
  [3, 1],
  [2, 2],
  [2, 4],
  [1, 3]
];

const PATH_LEN = SERPENT_PATH.length;
/** Soft tail length in path units (Braille-style ramp, not discrete steps). */
const TRAIL_SPAN = 4.25;

function isWithinTriangleMask(row: number, col: number): boolean {
  if (row < 0 || row >= MATRIX_SIZE || col < 0 || col >= MATRIX_SIZE) {
    return false;
  }

  return TRIANGLE_CELLS.has(`${row},${col}`);
}

function pathIndex(row: number, col: number): number | null {
  for (let i = 0; i < PATH_LEN; i += 1) {
    const [pr, pc] = SERPENT_PATH[i]!;
    if (pr === row && pc === col) {
      return i;
    }
  }
  return null;
}

function modF(n: number, m: number): number {
  return ((n % m) + m) % m;
}

function behindAlongPath(s: number, i: number, L: number): number {
  return modF(s - i, L);
}

function smoothstep01(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) {
    return x >= edge1 ? 1 : 0;
  }
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function opacityForCell(row: number, col: number, phase: number): number {
  const idx = pathIndex(row, col);
  if (idx === null) {
    return 0;
  }

  const s = phase * PATH_LEN;
  const d = behindAlongPath(s, idx, PATH_LEN);
  const g = 1 - smoothstep01(0, TRAIL_SPAN, d);
  return BASE_OPACITY + g * (HIGH_OPACITY - BASE_OPACITY);
}

export function DotmTriangle13({
  size = 30,
  dotSize = 6.5,
  color = "currentColor",
  ariaLabel = "Loading",
  className,
  muted = false,
  dotClassName,
  speed = 1.65,
  animated = true,
  hoverAnimated = false,
  cellPadding,
  opacityBase,
  opacityMid,
  opacityPeak
}: DotmTriangle13Props) {
  const reducedMotion = useDotMatrixMotionDisabled();
  const { phase: matrixPhase, onMouseEnter, onMouseLeave } = useDotMatrixPhases({
    animated: Boolean(animated && !reducedMotion),
    hoverAnimated: Boolean(hoverAnimated && !reducedMotion),
    speed
  });
  const cycleActive = !reducedMotion && matrixPhase !== "idle";
  const cyclePhase = useCyclePhase({
    active: cycleActive,
    cycleMsBase: 1400,
    speed
  });

  const gap =
    cellPadding ?? Math.max(1, Math.floor((size - dotSize * MATRIX_SIZE) / (MATRIX_SIZE - 1)));
  const matrixSize = dotSize * MATRIX_SIZE + gap * (MATRIX_SIZE - 1);
  const rootStyle = {
    width: stylePx(cellPadding == null ? size : matrixSize),
    height: stylePx(cellPadding == null ? size : matrixSize),
    color
  } as CSSProperties;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={ariaLabel}
      className={cx("dmx-root", muted && "dmx-muted", className)}
      style={rootStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className="dmx-grid"
        style={{
          gap,
          gridTemplateColumns: `repeat(${MATRIX_SIZE}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${MATRIX_SIZE}, minmax(0, 1fr))`
        }}
      >
        {Array.from({ length: MATRIX_SIZE * MATRIX_SIZE }).map((_, index) => {
          const row = Math.floor(index / MATRIX_SIZE);
          const col = index % MATRIX_SIZE;
          const isActive = isWithinTriangleMask(row, col);

          const phase = reducedMotion || matrixPhase === "idle" ? 0.14 : cyclePhase;
          const opacity = isActive ? opacityForCell(row, col, phase) : 0;

          return (
            <span
              key={index}
              aria-hidden="true"
              className={cx("dmx-dot", !isActive && "dmx-inactive", dotClassName)}
              style={{
                width: stylePx(dotSize),
                height: stylePx(dotSize),
                opacity: styleOpacity(remapOpacityToTriplet(opacity, opacityBase, opacityMid, opacityPeak))
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
